import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useSession } from '../../lib/store';
import { Card, Notice, PageHeading, Row, Spinner } from '../../components/Shell';
import { QuoteTable } from '../../components/Money';
import { BookingStamp, PaymentStamp, Stamp } from '../../components/Stamp';
import { SelectField, TextArea, TextField } from '../../components/Field';
import { UpdateCard, UpdateComposer } from '../../components/VisitUpdate';
import { dateRange, dateTime, money, titleCase } from '../../lib/format';
import type {
  Blocker,
  Booking,
  ClientProfile,
  Payment,
  PaymentSummary,
  Pet,
  ServiceSummary,
  VisitReport,
} from '../../lib/types';

type Action = 'confirm' | 'decline' | 'cancel' | 'start' | 'complete' | 'recheck';

/**
 * The sitter's view of one booking: everything they need to decide, plus the
 * pet detail they would otherwise be flipping through a binder to find.
 */
export function AdminBooking() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const session = useSession();
  const tenantId = session.tenant?.tenantId;
  const timezone = session.tenant?.timezone;

  const [booking, setBooking] = useState<Booking | null>(null);
  const [pets, setPets] = useState<Pet[]>([]);
  const [service, setService] = useState<ServiceSummary | null>(null);
  const [blockers, setBlockers] = useState<Blocker[]>([]);
  const [client, setClient] = useState<ClientProfile | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [reports, setReports] = useState<VisitReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId || !bookingId) return;
    try {
      const detail = await api<{
        booking: Booking;
        pets: Pet[];
        service: ServiceSummary;
        blockers: Blocker[];
      }>(`/tenants/${tenantId}/bookings/${bookingId}`);

      const [clientResult, paymentResult, reportResult] = await Promise.all([
        api<{ client: ClientProfile }>(`/tenants/${tenantId}/clients/${detail.booking.clientId}`),
        api<{ payments: Payment[]; summary: PaymentSummary }>(
          `/tenants/${tenantId}/bookings/${bookingId}/payments`
        ),
        // An update is the warmest thing here and the least load-bearing: if
        // the call fails the booking still opens, without its history.
        api<{ reports: VisitReport[] }>(`/tenants/${tenantId}/bookings/${bookingId}/reports`).catch(
          () => ({ reports: [] as VisitReport[] })
        ),
      ]);

      setBooking(detail.booking);
      setPets(detail.pets);
      setService(detail.service);
      setBlockers(detail.blockers);
      setClient(clientResult.client);
      setPayments(paymentResult.payments);
      setSummary(paymentResult.summary);
      setReports(reportResult.reports);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'This booking could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, bookingId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-16">
        <Spinner label="Opening the booking" />
      </div>
    );
  }

  if (!booking || !tenantId || !bookingId) {
    return (
      <div className="mx-auto max-w-4xl px-5 py-16">
        <Notice tone="error">{error ?? 'This booking is not available.'}</Notice>
      </div>
    );
  }

  const act = (action: Action, extra: Record<string, unknown> = {}) => {
    setWorking(true);
    setError(null);
    setNotice(null);

    void api<{ awaiting?: string; owed?: number; refund?: { refundable: number; forfeited: number } }>(
      `/tenants/${tenantId}/bookings/${bookingId}/status`,
      { method: 'POST', body: { action, ...extra } }
    )
      .then(async (result) => {
        if (result.awaiting === 'payment') {
          setNotice(
            `Moved to awaiting payment. ${money(result.owed ?? 0)} is still due before this is confirmed.`
          );
        } else if (result.refund) {
          setNotice(
            `Cancelled. ${money(result.refund.refundable)} is refundable and ${money(result.refund.forfeited)} is forfeit under the notice policy.`
          );
        }
        await load();
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That change did not go through.'))
      .finally(() => setWorking(false));
  };

  const confirmPayment = (paymentId: string, outcome: 'received' | 'not_received') => {
    setWorking(true);
    void api(`/tenants/${tenantId}/payments/${paymentId}/confirm`, {
      method: 'POST',
      body: { outcome },
    })
      .then(load)
      .catch((err) => setError(err instanceof ApiError ? err.message : 'The payment could not be updated.'))
      .finally(() => setWorking(false));
  };

  const canConfirm = ['requested', 'awaiting_documents', 'awaiting_payment'].includes(booking.status);
  const canStart = booking.status === 'confirmed';
  const canComplete = ['confirmed', 'in_progress'].includes(booking.status);
  const canCancel = ['requested', 'awaiting_documents', 'awaiting_payment', 'confirmed'].includes(
    booking.status
  );
  // Updates open once a stay is confirmed and close when it is over. Before
  // confirmation there is nothing to report; after completion the history
  // stays readable but the composer would only invite a note nobody is
  // waiting for.
  const canSendUpdate = ['confirmed', 'in_progress'].includes(booking.status);

  // Whether the button offers to text this update. Judged from the client's
  // own consent, which is what the page already has and what the sitter can
  // actually do something about. Whether the business itself has a working
  // number is the server's answer, and it gives a clear one on the send: this
  // page would have to fetch the whole tenant record to know it, for a case
  // that resolves itself the moment texting is turned on in settings.
  const textingAvailable = Boolean(
    client?.mobile && client.messaging?.smsConsent && !client.messaging.optedOutAt
  );
  const textingReason = client?.messaging?.optedOutAt
    ? `${client.fullName.split(' ')[0]} has asked not to receive texts. They can still read this in their portal.`
    : !client?.mobile
      ? 'No mobile number on file, so this stays in their portal.'
      : `${client?.fullName.split(' ')[0] ?? 'They'} has not opted in to texts. They can read this in their portal.`;

  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 pt-12">
      <PageHeading
        eyebrow={client?.fullName ?? 'Booking'}
        title={dateRange(booking.startDate, booking.endDate, timezone)}
        actions={<BookingStamp status={booking.status} />}
      />

      {notice && (
        <div className="mb-8">
          <Notice tone="info">{notice}</Notice>
        </div>
      )}
      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {blockers.length > 0 && (
        <div className="mb-8">
          <Notice tone="warn" title="Paperwork outstanding">
            <ul className="mt-1 space-y-1">
              {blockers.map((blocker) => (
                <li key={`${blocker.code}-${blocker.petId ?? ''}`}>
                  {blocker.message}
                  {!blocker.clientActionable && (
                    <span className="ml-2 font-mono text-xs text-[color:var(--color-stamp)]">Yours to clear</span>
                  )}
                </li>
              ))}
            </ul>
          </Notice>
        </div>
      )}

      {/* --- Actions ------------------------------------------------------- */}
      <Card className="mb-8 p-5">
        <div className="flex flex-wrap gap-3">
          {canConfirm && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => act('confirm')}
              disabled={working}
            >
              Confirm
            </button>
          )}
          {canConfirm && blockers.length > 0 && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => act('confirm', { overrideBlockers: true, overridePayment: true })}
              disabled={working}
              title="Confirms despite outstanding paperwork and payment. Recorded in the log."
            >
              Confirm anyway
            </button>
          )}
          {canStart && (
            <button type="button" className="btn btn-secondary" onClick={() => act('start')} disabled={working}>
              Mark as arrived
            </button>
          )}
          {canComplete && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => act('complete')}
              disabled={working}
            >
              Mark as finished
            </button>
          )}
          {booking.status === 'awaiting_documents' && (
            <button
              type="button"
              className="btn btn-quiet"
              onClick={() => act('recheck')}
              disabled={working}
            >
              Re-check paperwork
            </button>
          )}
          {canConfirm && <DeclineForm onDecline={(reason) => act('decline', { reason })} busy={working} />}
          {canCancel && (
            <button type="button" className="btn btn-danger" onClick={() => act('cancel')} disabled={working}>
              Cancel
            </button>
          )}
        </div>
      </Card>

      <div className="grid gap-6 sm:grid-cols-2">
        <Card tab="The stay" className="p-5">
          <dl>
            <Row label="Service">{service?.name ?? titleCase(booking.serviceCode)}</Row>
            <Row label="Drop off">{dateTime(booking.startAt, timezone)}</Row>
            <Row label="Pick up">{dateTime(booking.endAt, timezone)}</Row>
            {booking.nights > 0 && <Row label="Nights">{booking.nights}</Row>}
            {booking.visitCount && <Row label="Visits">{booking.visitCount}</Row>}
          </dl>
          {booking.clientNotes && (
            <div className="mt-4 border-t border-[color:var(--color-rule)] pt-3">
              <p className="field-label">From the client</p>
              <p className="text-sm">{booking.clientNotes}</p>
            </div>
          )}
        </Card>

        <Card tab="Client" className="p-5">
          <dl>
            <Row label="Name">{client?.fullName ?? '-'}</Row>
            <Row label="Mobile">{client?.mobile ?? '-'}</Row>
            <Row label="Email">{client?.email ?? '-'}</Row>
            {client?.emergencyContact && (
              <Row label="Emergency">
                {client.emergencyContact.name}, {client.emergencyContact.mobile}
              </Row>
            )}
            {client?.vet && (
              <Row label="Vet">
                {client.vet.name}, {client.vet.phone}
              </Row>
            )}
          </dl>
        </Card>
      </div>

      {/* --- Pet care sheet -------------------------------------------------- */}
      <section className="mt-8">
        <h2 className="mb-4 text-2xl">Care sheet</h2>
        <div className="grid gap-5 sm:grid-cols-2">
          {pets.map((pet) => (
            <Card key={pet.petId} tab={pet.name} className="p-5">
              <div className="mb-3 flex flex-wrap gap-2">
                {pet.medical.medicationRequired && (
                  <Stamp tone="pending" flat>
                    Medication
                  </Stamp>
                )}
                {pet.aggression.hasShownAggression && (
                  <Stamp tone="flag" flat>
                    Handle with care
                  </Stamp>
                )}
                {pet.specialNeeds.includes('escape_artist') && (
                  <Stamp tone="flag" flat>
                    Escape artist
                  </Stamp>
                )}
              </div>

              <dl>
                <Row label="Breed">{pet.breed ?? '-'}</Row>
                <Row label="Feeds">{pet.feeding.times ?? 'Not given'}</Row>
                <Row label="Amount">{pet.feeding.quantity ?? 'Not given'}</Row>
                {pet.medical.medicationRequired && (
                  <Row label="Medication">{pet.medical.medicationDetails ?? 'See file'}</Row>
                )}
                {pet.medical.allergies && <Row label="Allergies">{pet.medical.allergies}</Row>}
                {pet.temperament.bedtimeRoutine && <Row label="Bedtime">{pet.temperament.bedtimeRoutine}</Row>}
                {pet.temperament.loves && <Row label="Loves">{pet.temperament.loves}</Row>}
                {pet.temperament.hates && <Row label="Hates">{pet.temperament.hates}</Row>}
              </dl>

              {pet.aggression.hasShownAggression && pet.aggression.details && (
                <div className="mt-3 border-t border-[color:var(--color-rule)] pt-3">
                  <p className="field-label">Disclosed history</p>
                  <p className="text-sm">{pet.aggression.details}</p>
                </div>
              )}
            </Card>
          ))}
        </div>
      </section>

      {/* --- Updates home ---------------------------------------------------- */}
      {/* The care sheet above says what to do; this is where she says what she
          did. It sits between the care sheet and the money because that is the
          order of her day, and because the money is the last thing she should
          have to scroll past to reach the part her clients actually wait for. */}
      {(canSendUpdate || reports.length > 0) && (
        <section className="mt-10">
          <h2 className="mb-2 text-2xl">
            {canSendUpdate ? `Send ${client?.fullName.split(' ')[0] ?? 'them'} an update` : 'Updates you sent'}
          </h2>
          {canSendUpdate && (
            <p className="mb-5 text-sm text-[color:var(--color-ink-soft)]">
              A photo and a line about their day. This is the thing people remember.
            </p>
          )}

          {canSendUpdate && tenantId && bookingId && (
            <UpdateComposer tenantId={tenantId} bookingId={bookingId} onPosted={load} />
          )}

          {reports.length > 0 && (
            <ul className={`space-y-5 ${canSendUpdate ? 'mt-6' : ''}`}>
              {reports.map((report) => (
                <li key={report.reportId}>
                  <UpdateCard
                    report={report}
                    timezone={timezone}
                    texting={
                      tenantId && bookingId
                        ? {
                            tenantId,
                            bookingId,
                            available: textingAvailable,
                            unavailableReason: textingReason,
                          }
                        : undefined
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {/* --- Money ----------------------------------------------------------- */}
      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <Card tab="Price" className="p-5">
          <QuoteTable quote={booking.quote} compact />
        </Card>

        <Card tab="Payments" tint="yellow" className="p-5">
          <div className="flex items-center justify-between gap-3">
            <p className="font-display text-lg font-bold">
              {money(summary?.outstanding ?? 0)} outstanding
            </p>
            <PaymentStamp status={booking.payment.status} flat />
          </div>

          {payments.length === 0 ? (
            <p className="mt-4 text-sm text-[color:var(--color-ink-soft)]">Nothing recorded yet.</p>
          ) : (
            <ul className="mt-4 space-y-3">
              {payments.map((payment) => (
                <li key={payment.paymentId} className="border-b border-[color:var(--color-rule)] pb-3 last:border-b-0">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span className="typed">{money(payment.amount + payment.surcharge)}</span>
                    <span className="text-[color:var(--color-ink-soft)]">
                      {titleCase(payment.method)}, {titleCase(payment.kind)}
                    </span>
                  </div>
                  {payment.platformFee !== undefined && payment.platformFee > 0 && (
                    <p className="text-xs text-[color:var(--color-ink-faint)]">
                      {money(payment.platformFee)} of this is our fee, so{' '}
                      {money(payment.amount + payment.surcharge - payment.platformFee)} reaches you.
                    </p>
                  )}
                  {payment.clientReference && (
                    <p className="typed text-xs text-[color:var(--color-ink-faint)]">
                      Ref {payment.clientReference}
                    </p>
                  )}
                  {payment.status === 'awaiting_confirmation' ? (
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => confirmPayment(payment.paymentId, 'received')}
                        disabled={working}
                      >
                        It arrived
                      </button>
                      <button
                        type="button"
                        className="btn btn-quiet"
                        onClick={() => confirmPayment(payment.paymentId, 'not_received')}
                        disabled={working}
                      >
                        Never arrived
                      </button>
                    </div>
                  ) : (
                    <p className="mt-1 font-mono text-xs text-[color:var(--color-ink-faint)]">
                      {titleCase(payment.status)}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          <RecordPaymentForm
            tenantId={tenantId}
            bookingId={bookingId}
            outstanding={summary?.outstanding ?? 0}
            methods={(session.tenant?.acceptedPaymentMethods ?? []).filter((m) => m !== 'card')}
            onDone={load}
          />
        </Card>
      </div>
    </div>
  );
}

function DeclineForm({ onDecline, busy }: { onDecline: (reason: string) => void; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (!open) {
    return (
      <button type="button" className="btn btn-secondary" onClick={() => setOpen(true)} disabled={busy}>
        Decline
      </button>
    );
  }

  return (
    <div className="w-full space-y-3 border-t border-[color:var(--color-rule)] pt-4">
      <TextArea
        label="Why, in a sentence the client will read"
        value={reason}
        onChange={setReason}
        rows={2}
        placeholder="Those nights are already full with a private boarding."
      />
      <div className="flex gap-2">
        <button
          type="button"
          className="btn btn-danger"
          onClick={() => onDecline(reason)}
          disabled={busy || !reason.trim()}
        >
          Send the decline
        </button>
        <button type="button" className="btn btn-quiet" onClick={() => setOpen(false)}>
          Keep it open
        </button>
      </div>
    </div>
  );
}

/** The sitter recording cash or a transfer they have already received. */
function RecordPaymentForm({
  tenantId,
  bookingId,
  outstanding,
  methods,
  onDone,
}: {
  tenantId: string;
  bookingId: string;
  outstanding: number;
  methods: string[];
  onDone: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState(methods[0] ?? 'cash');
  const [amount, setAmount] = useState(String((outstanding / 100).toFixed(2)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (methods.length === 0) return null;

  if (!open) {
    return (
      <button type="button" className="btn btn-quiet mt-4" onClick={() => setOpen(true)}>
        Record a payment I received
      </button>
    );
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    void api(`/tenants/${tenantId}/bookings/${bookingId}/payments`, {
      method: 'POST',
      body: { method, amount: Math.round(Number(amount) * 100), kind: 'extra' },
    })
      .then(async () => {
        setOpen(false);
        await onDone();
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That could not be recorded.'))
      .finally(() => setBusy(false));
  };

  return (
    <form onSubmit={submit} className="mt-5 space-y-4 border-t border-[color:var(--color-rule)] pt-4">
      <SelectField
        label="How they paid"
        value={method}
        onChange={setMethod}
        options={methods.map((m) => ({ value: m, label: titleCase(m) }))}
      />
      <TextField label="Amount in dollars" type="number" value={amount} onChange={setAmount} />
      {error && <Notice tone="error">{error}</Notice>}
      <div className="flex gap-2">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Recording' : 'Record it'}
        </button>
        <button type="button" className="btn btn-quiet" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>
    </form>
  );
}
